import {
  CodeBlock,
  Heading,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  VStack,
} from '@astryxdesign/core';

/**
 * "Forecasting 500 SKUs."
 *
 * The timing pair comes from docs/marketing/assets/forecast-batch.json, a real
 * measurement written by scripts/brand/fetch-forecast.mjs: the same 96-point
 * series sent once, then four times in one request, on a free-tier key against
 * a CPU deployment. The plan ceilings are read from the API's plan catalogue —
 * the same numbers /pricing shows — not remembered.
 */

const SINGLE_MS = 1525;
const BATCH_MS = 2081;
const BATCH_N = 4;

const PLAN_CEILINGS = [
  { plan: 'Free', series: 4, rpm: 10, calls: '300 / month' },
  { plan: 'Starter', series: 32, rpm: 60, calls: '50,000 / month' },
  { plan: 'Pro', series: 128, rpm: 300, calls: '500,000 / month' },
  { plan: 'Enterprise', series: '512+', rpm: 2000, calls: 'unmetered' },
];

const DISCOVER = `import os, requests

API = "https://api.vectime.cloud"
H = {"Authorization": f"Bearer {os.environ['VECTIME_API_KEY']}"}

# Unmetered: does not count against the monthly quota.
model = requests.get(f"{API}/v1/models", headers=H, timeout=30).json()["models"][0]
MAX_SERIES  = model["max_series_per_request"]   # 4 / 32 / 128 / 512
MAX_CONTEXT = model["max_context"]              # 1024 / 2048 / 4096
MAX_HORIZON = model["max_horizon"]`;

const CHUNK = `def chunks(items, n):
    for i in range(0, len(items), n):
        yield items[i:i + n]

skus = list(catalogue.items())          # [(sku_id, [oldest, ..., newest]), ...]
results = {}

for batch in chunks(skus, MAX_SERIES):
    payload = {
        # Each series is trimmed to the plan's context window; a longer one is
        # rejected with context_limit_exceeded rather than silently truncated.
        "series": [hist[-MAX_CONTEXT:] for _, hist in batch],
        "horizon": 12,
        "quantiles": True,
    }
    r = requests.post(f"{API}/v1/forecast", headers=H, json=payload, timeout=120)
    r.raise_for_status()
    for (sku, _), fc in zip(batch, r.json()["forecasts"]):
        results[sku] = fc          # fc["index"] matches the position in "series"`;

const RAGGED = `# Series in one request may have different lengths. Each is validated on its
# own: at least 2 points, at most the plan's context window.
payload["series"] = [
    catalogue["A-100"][-1024:],   # a mature product, 4 years of weekly data
    catalogue["N-901"],           # a launch: 9 points, that is all there is
]`;

export function BatchForecastingApiArticle() {
  return (
    <>
      <Text>
        A catalogue of 500 SKUs is not 500 forecasting problems. It is one request per chunk of
        series, where the chunk size is a number your plan tells you and the model does the whole
        chunk in a single pass. On the Pro plan that is four requests. This post shows the one
        measurement we have of what batching saves, the loop that chunks a catalogue to the plan
        ceiling, and the two things that go wrong when people write that loop themselves.
      </Text>

      <div className="callout">
        <Text weight="medium">The short version</Text>
        <Text color="secondary">
          One series took {(SINGLE_MS / 1000).toFixed(2)} s of inference. Four series in one request
          took {(BATCH_MS / 1000).toFixed(2)} s — four times the work for {(BATCH_MS / SINGLE_MS).toFixed(2)}×
          the time, or {Math.round(BATCH_MS / BATCH_N)} ms per series. Read your ceiling from{' '}
          <span className="mono">GET /v1/models</span>, chunk to it, and one request is one metered
          call regardless of how many series it carries.
        </Text>
      </div>

      <VStack gap={2}>
        <Heading level={2} id="why-batch">Why one request beats five hundred</Heading>
        <Text color="secondary">
          Two reasons, and they are independent. The first is cost: a request is the unit of
          metering, so 500 SKUs in four requests spend four calls of quota, not five hundred. The
          second is time: a foundation model forecasts a batch of series in a single forward pass, so
          the fixed cost of the pass — loading the context, running the layers — is paid once. Per
          series, that cost shrinks as the batch grows, right up to the point where the batch no
          longer fits the hardware.
        </Text>
        <Text color="secondary">
          Neither reason depends on the series being related. The model does not learn across the
          batch; each series is forecast on its own history. Batching is a transport optimisation,
          not a modelling one, which is also why you can mix unrelated series freely.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="the-measurement">The measurement</Heading>
        <Text color="secondary">
          One data point, honestly labelled. The same 96-point weekly series was sent once, then four
          copies of it were sent in a single request, on a free-tier key against the CPU deployment
          the marketing assets are rendered from. <span className="mono">inference_ms</span> is the
          model time the API reports in every response; the wall-clock latency was about 50 ms more
          in both cases.
        </Text>
        <Table density="balanced" dividers="rows">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Request</TableHeaderCell>
              <TableHeaderCell>inference_ms</TableHeaderCell>
              <TableHeaderCell>Per series</TableHeaderCell>
              <TableHeaderCell>Metered calls</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>
                <Text size="sm">1 series × 96 points, horizon 12</Text>
              </TableCell>
              <TableCell>
                <span className="mono">{SINGLE_MS}</span>
              </TableCell>
              <TableCell>
                <span className="mono">{SINGLE_MS} ms</span>
              </TableCell>
              <TableCell>
                <Text size="sm">1</Text>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Text size="sm">4 series × 96 points, horizon 12</Text>
              </TableCell>
              <TableCell>
                <span className="mono">{BATCH_MS}</span>
              </TableCell>
              <TableCell>
                <span className="mono">{Math.round(BATCH_MS / BATCH_N)} ms</span>
              </TableCell>
              <TableCell>
                <Text size="sm">1</Text>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Text size="sm" color="secondary">
          What this does not show: how the curve bends at 32, 128 or 512 series, or what a GPU
          deployment does to the absolute numbers. Four is the free tier&apos;s ceiling and the free
          tier is what we measure with, so that the measurement is one you can repeat. If you run it
          at a larger batch, the per-series figure should keep falling until the batch saturates the
          device — but that is the expectation, not a measurement, and this table does not claim it.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="chunking">Chunking to your plan ceiling</Heading>
        <Text color="secondary">
          The ceiling differs by plan, and the right way to learn it is to ask the API rather than
          copy it from the pricing page into a constant that goes stale on the day you upgrade.
        </Text>
        <Table density="compact" dividers="rows">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Plan</TableHeaderCell>
              <TableHeaderCell>Series / request</TableHeaderCell>
              <TableHeaderCell>Requests / minute</TableHeaderCell>
              <TableHeaderCell>500 SKUs</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {PLAN_CEILINGS.map((p) => (
              <TableRow key={p.plan}>
                <TableCell>
                  <Text size="sm">{p.plan}</Text>
                </TableCell>
                <TableCell>
                  <span className="mono">{p.series}</span>
                </TableCell>
                <TableCell>
                  <span className="mono">{p.rpm}</span>
                </TableCell>
                <TableCell>
                  <Text size="sm">
                    {typeof p.series === 'number' ? `${Math.ceil(500 / p.series)} requests` : '1 request'}
                  </Text>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="code-frame">
          <CodeBlock code={DISCOVER} language="python" hasCopyButton container="card" />
        </div>
        <div className="code-frame">
          <CodeBlock code={CHUNK} language="python" hasCopyButton container="card" />
        </div>
        <Text color="secondary">
          Two details in that loop are load-bearing. The results come back in the order the series
          went in, with an <span className="mono">index</span> on each, so the zip is safe — but keep
          the index check if you ever filter series out before sending. And the history is trimmed to
          the context window on the client, because the server rejects a series that is too long with{' '}
          <span className="mono">context_limit_exceeded</span> instead of quietly dropping the oldest
          points. That is deliberate: silent truncation would make a forecast depend on a limit you
          never saw.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="ragged-histories">Ragged histories</Heading>
        <Text color="secondary">
          A real catalogue has products with four years of history next to products launched last
          quarter. They can share a request. Each series is validated on its own length — at least two
          points, at most the context window — so there is no padding to do and no need to sort the
          catalogue by history length first.
        </Text>
        <div className="code-frame">
          <CodeBlock code={RAGGED} language="python" hasCopyButton container="card" />
        </div>
        <Text color="secondary">
          What you should expect from the nine-point series is a flat median and a wide band. That is
          the model declining to extrapolate from almost nothing, and it is the correct answer; we
          have a{' '}
          <Link href="/blog/quantile-forecasting-explained#when-quantiles-are-not-enough">
            note on what to do instead
          </Link>{' '}
          when the history is that short.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="throughput">Throughput and the rate limit</Heading>
        <Text color="secondary">
          Batching also changes the arithmetic on the per-minute limit. The limit counts requests, not
          series, so at 128 series per request the Pro plan&apos;s 300 requests a minute is 38,400
          series a minute before the bucket empties — far more than the model will return in that
          time. In practice the model, not the limiter, is the bottleneck for a batched client, and
          you will hit <span className="mono">503 model_unavailable</span> on a cold start long before
          you hit a 429. Both are retryable; the{' '}
          <Link href="/blog/forecast-api-rate-limits">rate-limit post</Link> has the loop.
        </Text>
        <Text color="secondary">
          Run chunks sequentially unless you have measured that concurrency helps. On a CPU
          deployment two requests in flight share the same cores, and the second one mostly waits.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="when-not-to-batch">When not to batch</Heading>
        <VStack gap={1.5}>
          <Text color="secondary">
            <strong>You need one answer now.</strong> A forecast behind an interactive request should
            go alone. Waiting to fill a batch adds latency to the one series someone is looking at.
          </Text>
          <Text color="secondary">
            <strong>The series have different horizons.</strong> The horizon is per request. Two
            series that need 6 and 52 steps go in two requests, or in one at 52 with the extra steps
            discarded — which spends forecast points you did not need.
          </Text>
          <Text color="secondary">
            <strong>One bad series should not fail the rest.</strong> A request is validated as a
            whole: a single series with a NaN in it rejects all 128 with{' '}
            <span className="mono">invalid_request</span>. Clean the batch first, or accept that the
            retry after a validation error re-sends everything.
          </Text>
        </VStack>
      </VStack>
    </>
  );
}
